import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Pencil, Plus, Stethoscope } from 'lucide-react';
import { procedureApi } from '../services/procedureApi';
import ServiceGroupsList from '../components/ServiceGroupsList';
import ProcedureMappingDialog from '../components/ProcedureMappingDialog';
import NewProcedureModal from '../components/NewProcedureModal';
import type { GroupFilter } from '../components/ServiceGroupsList';
import type { ProcedureWithGroupDto } from '../types/procedure';
import './ProceduresPage.css';

export default function ProceduresPage() {
  const [selectedFilter, setSelectedFilter] = useState<GroupFilter>({ type: 'all' });
  const [searchText, setSearchText] = useState('');
  const [selectedProcedure, setSelectedProcedure] = useState<ProcedureWithGroupDto | null>(null);
  const [showNewModal, setShowNewModal] = useState(false);

  const { data: allProcedures, isLoading, error, refetch } = useQuery({
    queryKey: ['procedures'],
    queryFn: () => procedureApi.getProcedures(),
  });

  // Compute counts per group for sidebar badges
  const procedureCounts = useMemo(() => {
    const counts = new Map<number | null, number>();
    if (!allProcedures) return counts;
    for (const proc of allProcedures) {
      const key = proc.serviceGroupId;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
  }, [allProcedures]);

  // Filter procedures based on sidebar selection and search
  const filteredProcedures = useMemo(() => {
    if (!allProcedures) return [];

    let filtered: ProcedureWithGroupDto[];
    switch (selectedFilter.type) {
      case 'all':
        filtered = allProcedures.filter((p) => p.isMapped);
        break;
      case 'group':
        filtered = allProcedures.filter((p) => p.serviceGroupId === selectedFilter.groupId);
        break;
      case 'unmapped':
        filtered = allProcedures.filter((p) => !p.isMapped);
        break;
    }

    // Apply text search
    if (searchText.trim()) {
      const term = searchText.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.procedureName.toLowerCase().includes(term) ||
          (p.displayName && p.displayName.toLowerCase().includes(term)) ||
          (p.procedureCode && p.procedureCode.toLowerCase().includes(term))
      );
    }

    // Sort: by group name, then by displayOrder, then by name
    filtered.sort((a, b) => {
      const ga = a.groupName ?? '';
      const gb = b.groupName ?? '';
      if (ga !== gb) return ga.localeCompare(gb);
      if (a.displayOrder !== b.displayOrder) return a.displayOrder - b.displayOrder;
      return a.procedureName.localeCompare(b.procedureName);
    });

    return filtered;
  }, [allProcedures, selectedFilter, searchText]);

  if (error) return <div className="procedures-empty">Error loading procedures</div>;

  return (
    <div className="procedures-page">
      <div className="procedures-header">
        <div className="procedures-title-row">
          <div className="page-title-row">
            <Stethoscope size={28} />
            <h1>Procedure Mapping</h1>
          </div>
          <button
            type="button"
            className="procedures-add-btn"
            onClick={() => setShowNewModal(true)}
          >
            <Plus size={16} />
            New Procedure
          </button>
        </div>
        <div className="procedures-search-row">
          <div className="procedures-search">
            <input
              type="text"
              placeholder="Search procedures..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="search-input"
              aria-label="Search procedures"
            />
            <Search size={18} className="search-icon" />
          </div>
        </div>
        {!isLoading && allProcedures && (
          <div className="procedures-results-count">
            {filteredProcedures.length} {filteredProcedures.length === 1 ? 'procedure' : 'procedures'}
            {searchText && ` matching "${searchText}"`}
          </div>
        )}
      </div>

      <div className="procedures-layout">
        <aside className="groups-sidebar">
          <ServiceGroupsList
            selectedFilter={selectedFilter}
            onSelectFilter={setSelectedFilter}
            procedureCounts={procedureCounts}
          />
        </aside>
        <main className="procedures-main">
          {isLoading ? (
            <div className="procedures-loading">
              <div className="loading-spinner" />
              <p>Loading procedures...</p>
            </div>
          ) : filteredProcedures.length === 0 ? (
            <div className="procedures-empty">
              <p>{searchText ? `No procedures matching "${searchText}"` : 'No procedures found'}</p>
            </div>
          ) : (
            <div className="procedures-table-wrapper">
              <table className="procedures-table">
                <thead>
                  <tr>
                    <th className="col-name">Name</th>
                    <th className="col-display-name">Display Name</th>
                    <th className="col-group">Group</th>
                    <th className="col-order">Order</th>
                    <th className="col-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProcedures.map((proc) => (
                    <tr key={proc.procedureId}>
                      <td className="col-name">{proc.procedureName}</td>
                      <td className="col-display-name">{proc.displayName || '-'}</td>
                      <td className="col-group">
                        {proc.groupName ? (
                          <span className="group-tag group-tag-mapped">{proc.groupName}</span>
                        ) : (
                          <span className="group-tag group-tag-unmapped">Unmapped</span>
                        )}
                      </td>
                      <td className="col-order">{proc.isMapped ? proc.displayOrder : '-'}</td>
                      <td className="col-actions">
                        <button
                          type="button"
                          className={`action-btn ${proc.isMapped ? 'action-btn-edit' : 'action-btn-map'}`}
                          onClick={() => setSelectedProcedure(proc)}
                        >
                          {proc.isMapped ? (
                            <><Pencil size={12} /> Edit</>
                          ) : (
                            <><Plus size={12} /> Map</>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>

      {selectedProcedure && (
        <ProcedureMappingDialog
          procedure={selectedProcedure}
          onClose={() => setSelectedProcedure(null)}
          onSaved={() => {
            setSelectedProcedure(null);
            refetch();
          }}
        />
      )}

      {showNewModal && (
        <NewProcedureModal
          onClose={() => setShowNewModal(false)}
          onSaved={() => {
            setShowNewModal(false);
            refetch();
          }}
        />
      )}
    </div>
  );
}
